import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, ROLE_HOME } from "@/context/AuthContext";
import { useCountry, convertFromFCFA } from "@/context/CountryContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShoppingBag, Store, Wrench, MapPin } from "lucide-react";

const ROLES = [
  { key: "client", label: "Client", desc: "Comparer les prix & réserver", icon: ShoppingBag },
  { key: "commercant", label: "Commerçant", desc: "Vendre & gérer ma boutique", icon: Store },
  { key: "prestataire", label: "Prestataire", desc: "Proposer mes services", icon: Wrench },
];

export default function Register() {
  const { register } = useAuth();
  const { config } = useCountry();
  const navigate = useNavigate();
  const [role, setRole] = useState("client");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register({ ...form, role });
      toast.success("Compte créé !");
      if (role !== "client") toast.info("Votre compte pro est en attente de validation par un administrateur.");
      navigate(ROLE_HOME[u.role]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-12 animate-fade-up">
      <h1 className="font-display text-3xl font-bold text-secondary mb-1">Créer un compte</h1>
      <p className="text-muted-foreground mb-6 text-sm">Choisissez votre profil pour commencer.</p>

      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        {ROLES.map((r) => {
          const Icon = r.icon;
          const active = role === r.key;
          return (
            <button key={r.key} type="button" data-testid={`role-${r.key}`} onClick={() => setRole(r.key)}
              className={`text-left p-4 rounded-lg border transition-transform hover:-translate-y-1 ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card"}`}>
              <Icon className={`w-6 h-6 mb-2 ${active ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-semibold text-secondary">{r.label}</div>
              <div className="text-xs text-muted-foreground">{r.desc}</div>
            </button>
          );
        })}

        {/* Agent Terrain — candidature externe */}
        <Link to="/agent-candidature"
          className="text-left p-4 rounded-lg border border-dashed border-primary/40 bg-primary/3 hover:bg-primary/8 hover:border-primary transition-colors group sm:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-semibold text-secondary">Agent Terrain</div>
                <div className="text-xs text-muted-foreground">Enregistrer boutiques & prix — gagner des commissions</div>
              </div>
            </div>
            <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-full whitespace-nowrap">
              {new Intl.NumberFormat("fr-FR").format(convertFromFCFA(500, config))} {config.currency}/boutique →
            </span>
          </div>
        </Link>
      </div>

      <form onSubmit={submit} className="space-y-4 max-w-md">
        <div><Label>Nom complet</Label><Input data-testid="reg-name" value={form.name} onChange={set("name")} required /></div>
        <div><Label>Email</Label><Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required /></div>
        <div><Label>Téléphone</Label><Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="+225 ..." /></div>
        <div><Label>Mot de passe</Label><Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required /></div>
        <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90">
          {busy ? "Création…" : "Créer mon compte"}
        </Button>
      </form>
      <p className="text-sm mt-6 text-muted-foreground">Déjà inscrit ? <Link to="/login" className="text-primary font-medium">Connectez-vous</Link></p>
    </div>
  );
}
